import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const SectionContainer = styled.div`
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #01010110;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 1.5rem 2rem;
  background: #f1f1f1;
  border-bottom: 1px solid #01010110;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333333;
  margin: 0;
`;

const SectionContent = styled.div`
  padding: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333333;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #01010120;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: #f9f9f9;
  color: #333333;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00a693;
    box-shadow: 0 0 0 3px rgba(0, 166, 147, 0.1);
    background: white;
  }
`;

const ScheduleSection = ({
  date,
  startTime,
  endTime,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  duration,
  crossesMidnight
}) => {
  // Helper function to format time display
  const formatTimeDisplay = (time) => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${time} (${displayHours}:${minutes.toString().padStart(2, '0')} ${period})`;
  };

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Schedule</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <FormGrid>
          <FormField>
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => onDateChange(e.target.value)}
              required
            />
          </FormField>
          <FormField>
            <Label>Start Time {startTime && `- ${formatTimeDisplay(startTime)}`}</Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => onStartTimeChange(e.target.value)}
              required
            />
          </FormField>
          <FormField>
            <Label>End Time {endTime && `- ${formatTimeDisplay(endTime)}`}</Label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => onEndTimeChange(e.target.value)}
              required
            />
          </FormField>
        </FormGrid>
        
        {/* Duration and midnight crossing info */}
        {duration && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', border: '1px solid #e9ecef' }}>
            <div style={{ fontSize: '0.875rem', color: '#495057', marginBottom: '0.25rem' }}>
              <strong>Duration:</strong> {duration}
            </div>
            {crossesMidnight && (
              <div style={{ fontSize: '0.875rem', color: '#856404', background: '#fff3cd', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ffeaa7' }}>
                ⏰ This event crosses midnight to the next day
                <br />
                <small style={{ fontSize: '0.75rem' }}>
                  Actual schedule: {date} at {startTime} → {new Date(date).getDate() + 1}/{new Date(date).getMonth() + 1} at {endTime}
                </small>
              </div>
            )}
          </div>
        )}
        
        {/* Helpful guidance */}
        <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
          💡 <strong>Tip:</strong> To schedule an event that crosses midnight, set the end time to be earlier than the start time in 24-hour format. 
          <br />
          <strong>Examples:</strong> 11:00 PM to 1:00 AM = 23:00 to 01:00, 10:30 PM to 2:15 AM = 22:30 to 02:15
          <br />
          The system automatically detects midnight crossing and adjusts the end date to the next day.
        </div>
      </SectionContent>
    </SectionContainer>
  );
};

ScheduleSection.propTypes = {
  date: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onStartTimeChange: PropTypes.func.isRequired,
  onEndTimeChange: PropTypes.func.isRequired,
  duration: PropTypes.string,
  crossesMidnight: PropTypes.bool
};

export default ScheduleSection; 